import { User, Session } from './types';
import { ApiError, request, requestWithAuth } from '../../api/request';
import type { SignupContext } from '@shared/onboarding';

/**
 * The outcome of a session-validation call.
 *
 * `unauthenticated` is the ONLY value that authorises deleting the stored token: it means the
 * server explicitly answered 401 for /auth/me. `unavailable` means we could not find out (5xx,
 * a network error, the 10s axios timeout, a malformed response) and the token must be kept —
 * collapsing that case into "signed out" is what made a cold dyno look like a session expiry.
 */
export type CurrentUserResult =
  | { status: 'authenticated'; user: User }
  | { status: 'unauthenticated' }
  | { status: 'unavailable'; reason: string };

/**
 * Pure classifier for a thrown /auth/me failure. Only an ApiError carrying status 401 is a real
 * "token is dead" signal; everything else keeps the session alive. A network error or timeout
 * produces an ApiError whose `status` is undefined, which is precisely why the check is an
 * explicit `=== 401` rather than a truthiness or `>= 400` test.
 */
export const classifyCurrentUserFailure = (error: unknown): CurrentUserResult => {
  if (error instanceof ApiError && error.status === 401) {
    return { status: 'unauthenticated' };
  }

  if (error instanceof Error) {
    return { status: 'unavailable', reason: error.message || error.name };
  }

  return { status: 'unavailable', reason: String(error) };
};

export interface AuthResponse {
  user?: User;
  token?: string;
  error?: string;
}

class ClientAuthService {

  async signUp({ email, password, fullName, signupContext }: {
    email: string;
    password: string;
    fullName?: string;
    // Phase 49 (D-04): advisory; the backend allowlists it before storing.
    signupContext?: SignupContext;
  }): Promise<AuthResponse> {
    try {
      const data = await request<{ user: User; token: string }>({
        path: '/auth/signup',
        method: 'POST',
        data: { email, password, fullName, ...(signupContext ? { signupContext } : {}) },
      });

      return { user: data.user, token: data.token };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'حدث خطأ أثناء إنشاء الحساب' };
    }
  }

  async signIn({ email, password }: { 
    email: string; 
    password: string;
  }): Promise<AuthResponse> {
    try {
      const data = await request<{ user: User; token: string }>({
        path: '/auth/signin',
        method: 'POST',
        data: { email, password },
      });

      return { user: data.user, token: data.token };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'حدث خطأ أثناء تسجيل الدخول' };
    }
  }

  async getCurrentUser(token: string): Promise<CurrentUserResult> {
    try {
      const data = await request<{ user: User }>({
        path: '/auth/me',
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (data?.user) {
        return { status: 'authenticated', user: data.user };
      }

      // A 200 with no user is a contract violation, not proof the token is dead — a broken
      // response shape must never destroy a session.
      return { status: 'unavailable', reason: 'malformed /auth/me response' };
    } catch (error) {
      console.error('Get current user error:', error);
      return classifyCurrentUserFailure(error);
    }
  }

  async signInWithFacebook({ 
    email, 
    accessToken, 
    providerId, 
    fullName, 
    avatarUrl 
  }: {
    email: string;
    accessToken: string;
    providerId: string;
    fullName?: string;
    avatarUrl?: string;
  }): Promise<AuthResponse> {
    try {
      const data = await request<{ user: User; token: string }>({
        path: '/auth/facebook',
        method: 'POST',
        data: { email, accessToken, providerId, fullName, avatarUrl },
      });

      return { user: data.user, token: data.token };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'حدث خطأ أثناء تسجيل الدخول' };
    }
  }
}

export const clientAuthService = new ClientAuthService();
